import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

import { Button, Icon } from "@/design-system";

/**
 * The reference HOME's signed-out "New to Dens 'N Dente?" band (the dashed box after
 * the hero, `sc-if signedOut` — grep `New to Dens` in the reference). Copy is the
 * reference's own; only the CTA targets are mapped onto what exists:
 *
 * - "Browse as guest" → `/categories` (the reference's `goShop`).
 * - "Open an account" → Auth0 signup (`screen_hint: "signup"`, `AuthButton`'s pattern);
 *   the reference's `goApply` application page has no equivalent here.
 *
 * Renders `null` when signed in — the gate lives here, not in `LandingPage`, so the
 * page composition stays a flat list.
 */
export default function NewHereBand() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  if (isAuthenticated || isLoading) return null;

  return (
    <section className="mx-auto mb-[72px] mt-0 max-w-container px-[var(--gutter)] py-0">
      <div className="flex flex-col items-start gap-[var(--space-6)] rounded-xl border border-dashed border-line bg-card px-[var(--space-8)] py-[var(--space-8)] font-sans md:flex-row md:items-center">
        <span className="inline-flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-soft text-blue-600">
          <Icon name="waving_hand" size={28} weight={400} />
        </span>
        <div className="flex-1">
          <div className="text-xl font-semibold text-ink">
            New to Dens &apos;N Dente?
          </div>
          <p className="mb-0 mt-[var(--space-1)] text-ink-2">
            First-time buyer after a hard-to-find specialty item? Order it today
            as a one-off — no account required. Running a clinic? Open an
            account for Net 30 terms, saved lists and one-tap reordering.
          </p>
        </div>
        {/* `md:shrink-0` only: on a phone the pair is wider than the card's inner
            width (measured 376px vs ~278px at 390px viewport — the page's only
            horizontal overflow), so below `md` it wraps instead. */}
        <div className="flex w-full flex-wrap gap-[10px] md:w-auto md:shrink-0">
          <Button variant="outline" onClick={() => navigate("/categories")}>
            Browse as guest
          </Button>
          <Button
            startIcon="add_business"
            onClick={() => loginWithRedirect({ screen_hint: "signup" })}
          >
            Open an account
          </Button>
        </div>
      </div>
    </section>
  );
}
